import { ltr } from 'semver';

import {
  GenericHttpError,
  type GenericHttpRequestOptions,
  type GenericHttpTransport,
  type HttpMethod,
} from '@iconik-io/core/src/http/generic_http_transport';
import { isObject } from '@iconik-io/core/src/is_object';
import { getPlatformPath } from '@iconik-io/core/src/os/get_platform_path';
import type { FilesFormatSchemaWithId } from '@iconik-io/iconik-api/src/services/files';

import type { AgentFeature } from './features';
import { AGENT_HTTP_PORT, AGENT_HTTPS_PORT } from './ports';

export type AutoLoginHeadersBase = {
  'Iconik-Host': string;
  'App-ID': string;
};

export type AutoLoginHeadersWithAuthToken = AutoLoginHeadersBase & {
  'Auth-Token': string;
  'Share-Hash'?: never;
  'Share-Auth-Token'?: never;
};

export type AutoLoginHeadersWithShareHash = AutoLoginHeadersBase & {
  'Share-Hash': string;
  'Share-Auth-Token'?: never;
  'Auth-Token'?: never;
};

export type AutoLoginHeadersWithShareToken = AutoLoginHeadersBase & {
  'Share-Auth-Token': string;
  'Share-Hash'?: never;
  'Auth-Token'?: never;
};

export type AutoLoginHeaders =
  | AutoLoginHeadersWithAuthToken
  | AutoLoginHeadersWithShareHash
  | AutoLoginHeadersWithShareToken;

const PROTOCOL_TO_PORT_MAP: Record<'http' | 'https', number> = {
  http: AGENT_HTTP_PORT,
  https: AGENT_HTTPS_PORT,
};

/**
 * Compute the agent base URL, using HTTPS on Safari
 */
export function agentBaseUrl(): string {
  const isSafari =
    typeof window !== 'undefined' &&
    window.navigator.userAgent.includes('Safari') &&
    !window.navigator.userAgent.includes('Chrome');

  // Safari does not allow http to localhost so the Agent runs a
  // https server as well along with the http one. Use https when in Safari.
  const protocol = isSafari ? 'https' : 'http';
  const port = PROTOCOL_TO_PORT_MAP[protocol];
  return `${protocol}://127.0.0.1:${port}`;
}

/**
 * `DOWNLOAD_ALL_FORMATS`
 *
 * Special format name used to trigger a download of all formats for an asset.
 * Send instead of for example ORIGINAL or PPRO_PROXY. Will be handled separately
 * by the Agent to fetch all formats for the asset and download them.
 */
export type DownloadItemFormat =
  | 'ICONIK_PROXY'
  | 'ORIGINAL'
  | 'SUBTITLES'
  | 'DOWNLOAD_ALL_FORMATS'
  | FilesFormatSchemaWithId['name'];

/**
 * Agent metadata returned by the /ping endpoint
 */
type AgentMetadata = {
  version: string;
  features: AgentFeature[];
};

/**
 * Download API types
 */
export type DownloadAsset = {
  assetId: string;
  format: DownloadItemFormat;
  formatId?: string;
  outputPath?: string;
};

export type DownloadCollection = {
  collectionId: string;
  format: DownloadItemFormat;
};

export type DownloadPlaylist = {
  playlistId: string;
  format: DownloadItemFormat;
};

export type DownloadFileSet = {
  assetId: string;
  fileSetId: string;
};

export type DownloadAssetVersion = {
  assetId: string;
  versionId: string;
  format: DownloadItemFormat;
  formatId?: string;
};

export type DownloadFile = {
  assetId: string;
  fileId: string;
  outputPath?: string;
};

export type DownloadRequestBody = {
  assets?: DownloadAsset[];
  collections?: DownloadCollection[];
  playlists?: DownloadPlaylist[];
  fileSets?: DownloadFileSet[];
  assetVersions?: DownloadAssetVersion[];
  files?: DownloadFile[];
};

export type DownloadRequest = {
  id: string;
  title: string;
  objectIdentity: any;
};

type DownloadRequests = {
  objects: DownloadRequest[];
};

type DownloadFileItem = {
  fileId: string;
  fileName: string;
  fullPath: string;
  downloaded: number;
  size?: number;
};

type DownloadItemStatus = {
  type: 'enqueued' | 'resolving' | 'active' | 'paused' | 'completed' | 'failed';
  code?: string;
  message?: string;
};

type DownloadItemIdentity = {
  type: string;
  [key: string]: any;
};

export type DownloadItem = {
  id: string;
  requestItemId: string;
  files: DownloadFileItem[];
  status: DownloadItemStatus;
  objectIdentity: DownloadItemIdentity;

  // TODO Following fields are not exposed in JSON schemas http://localhost:6286/docs/json
  estimatedTotalSize?: number;
  title: string;
};

type DownloadItems = {
  objects: DownloadItem[];
};

/**
 * Upload API types
 */
type UploadTargetAssetNew = {
  type: 'newAsset';
  assetId?: string;
};
type UploadTargetAssetVersion = {
  type: 'newVersion';
  assetId: string;
  versionId?: string;
};
type UploadTargetAssetExisting = {
  type: 'existingAsset';
  assetId: string;
  fillsPlaceholder?: boolean;
};
type UploadTargetAsset =
  | UploadTargetAssetNew
  | UploadTargetAssetVersion
  | UploadTargetAssetExisting;

type UploadRequestBody = {
  filePaths: string[];
  targetAsset: UploadTargetAsset;
  destinationStorageId?: string;
  destinationCollectionId?: string;
};

type UploadRequestItem = {
  id: string;
  type: 'file' | 'directory';
  title: string;
  assetIds: string[];
};

type UploadRequestItems = {
  objects: UploadRequestItem[];
};

type UploadChunk = {
  index: number;
  status: 'WAITING' | 'UPLOADING' | 'COMPLETED' | 'FAILED';
  start: number;
  size: number;
  uploaded: number;
};

type UploadAssetItemState = {
  id: string;
  fileInfo: { targetPath: string; size: number; mimeType: string };
  target: UploadTargetAsset;
  /** @deprecated Use target instead. Carries the same value. */
  targetAsset: UploadTargetAsset;
  formatId?: string;
  fileSetId?: string;
  fileId?: string;
  status:
    | 'WAITING'
    | 'UPLOADING'
    | 'COMPLETED'
    | 'COMPLETING'
    | 'PAUSED'
    | 'FAILED';
  chunks?: UploadChunk[];
};

type UploadAssetItem = {
  id: string;
  uploadRequestId: string;
  error?: { code: string; message: string };
  state: UploadAssetItemState;
};

type UploadAssetItems = {
  objects: UploadAssetItem[];
};

type DownloadRequestParams = {
  open_in_fcp?: number;
  resolve_on_local_isg?: number;
};

export type TryDownloadOptions = {
  body: DownloadRequestBody;
  params?: DownloadRequestParams;
  autoLoginHeaders?: AutoLoginHeaders;
};

export type TryDownloadRequestResponse =
  | {
      type: 'agent_not_running';
    }
  | {
      type: 'success';
      requests: DownloadRequest[];
    }
  | { type: 'error'; error: Error };

export type OAuthSessionPollResult =
  | { type: 'agent_not_running' }
  | { type: 'pending' }
  | { type: 'complete'; code: string }
  | { type: 'error'; error: string; errorDescription?: string };

type OAuthSessionResultBody = {
  status: 'complete' | 'error';
  code?: string;
  error?: string;
  error_description?: string;
};

function prepareOutputPath<Item extends { outputPath?: string }>(
  items: Item[],
) {
  return items.map((item) => ({
    ...item,
    outputPath: getPlatformPath(item.outputPath),
  }));
}

type AgentRequestConfig = {
  body?: unknown;
  query?: unknown;
  headers?: Record<string, string>;
};

const toHeaders = (headers: AutoLoginHeaders | undefined) =>
  Object.fromEntries(
    Object.entries(headers ?? {}).filter(
      (entry): entry is [string, string] => entry[1] !== undefined,
    ),
  );

const MIN_AGENT_VERSION = '25.2.0';

/**
 * The first Agent that answers a download request with the request it created.
 * Earlier ones start the download but reply 500, so there is no id to poll or
 * cancel by.
 */
export const MIN_TRACKABLE_DOWNLOAD_AGENT_VERSION = '25.9.0';

/** Whether this Agent reports the request ids a download can be followed by. */
export function reportsDownloadRequestIds(version: string): boolean {
  return !ltr(version, MIN_TRACKABLE_DOWNLOAD_AGENT_VERSION);
}

export class OutdatedAgentError extends Error {
  constructor() {
    super(
      'The iconik Agent version is outdated. Please update the Agent to a newer version.',
    );
  }
}

/**
 * HTTP client for the Agent API
 */
export class AgentApiClient {
  #metadata?: AgentMetadata;
  #autoLoginHeaders?: AutoLoginHeaders;

  constructor(
    private readonly transport: GenericHttpTransport,
    private readonly baseUrl: string = agentBaseUrl(),
  ) {}

  /**
   * Run ping to fetch agent metadata (version and features). Times out after 200ms.
   */
  async ping(): Promise<AgentMetadata | undefined> {
    let metadata;
    try {
      const response = await this.transport(
        { method: 'get', url: `${this.baseUrl}/ping` },
        { timeoutMs: 200 },
      );
      // biome-ignore lint/plugin: the Agent's /ping response is trusted to match AgentMetadata.
      metadata = response.body as AgentMetadata;
    } catch {
      metadata = undefined;
    }

    this.#metadata = metadata;
    return metadata;
  }

  get isReachable(): boolean {
    return Boolean(this.#metadata);
  }

  /**
   * Get agent features from metadata
   */
  get features(): AgentFeature[] {
    return this.#metadata?.features ?? [];
  }

  /**
   * Get agent version from metadata
   */
  get version(): string {
    return this.#metadata?.version ?? '';
  }

  hasFeature(feature: AgentFeature): boolean {
    return this.features.includes(feature);
  }

  /**
   * Set auto login headers for download requests
   */
  setAutoLoginHeaders(headers: AutoLoginHeaders): void {
    this.#autoLoginHeaders = headers;
  }

  // ===== Download endpoints =====

  async apiListDownloads(): Promise<DownloadItems> {
    return this.#agentRequest<DownloadItems>('get', '/downloads');
  }

  async apiGetDownload(id: string): Promise<DownloadItem> {
    return this.#agentRequest<DownloadItem>('get', `/downloads/${id}`);
  }

  async apiPatchDownload(
    id: string,
    patch: Partial<Pick<DownloadItem, 'status'>>,
  ): Promise<DownloadItem> {
    return this.#agentRequest<DownloadItem>('patch', `/downloads/${id}`, {
      body: { status: patch.status },
    });
  }

  async apiListDownloadRequests(): Promise<DownloadRequests> {
    return this.#agentRequest<DownloadRequests>('get', '/download-requests');
  }

  async apiCreateDownloadRequest(
    body: DownloadRequestBody,
    params?: DownloadRequestParams,
  ): Promise<DownloadRequest[]> {
    // We are using newer Agent API that was added in Agent 25.2.0 so we need to check
    // that the Agent is new enough.
    if (ltr(this.version, MIN_AGENT_VERSION)) {
      throw new OutdatedAgentError();
    }

    const autoLoginHeaders = this.hasFeature('AUTO_LOGIN')
      ? this.#autoLoginHeaders
      : undefined;

    if (autoLoginHeaders?.['Share-Hash'] && !this.hasFeature('SHARES')) {
      throw new Error('Share downloads are not supported by Iconik Agent.');
    }

    return this.#agentRequest<DownloadRequest[]>('post', '/download-requests', {
      body,
      query: params,
      headers: toHeaders(autoLoginHeaders),
    }).catch((error) => {
      // Agent versions prior to 25.9.0 incorrectly returns 500 even though the request downloads.
      if (
        error instanceof GenericHttpError &&
        error.response.status === 500 &&
        isObject(error.response.body) &&
        error.response.body.message === '"id" is required!' &&
        !reportsDownloadRequestIds(this.version)
      ) {
        return [];
      } else {
        throw error;
      }
    });
  }

  async apiGetDownloadRequest(id: string): Promise<DownloadRequest> {
    return this.#agentRequest<DownloadRequest>(
      'get',
      `/download-requests/${id}`,
    );
  }

  /**
   * Cancel an agent download by request ID. This will cancel all the downloads associated with the request ID
   * so if you have for example requested a collection download then it might cancel multiple asset downloads.
   */
  async apiCancelDownloadRequest(id: string): Promise<void> {
    return this.#agentRequest<void>('delete', `/download-requests/${id}`);
  }

  async apiListDownloadsByRequest(
    id: string,
    options?: GenericHttpRequestOptions,
  ): Promise<DownloadItems> {
    return this.#agentRequest<DownloadItems>(
      'get',
      `/download-requests/${id}/downloads`,
      {},
      options,
    );
  }

  // ===== Upload endpoints =====
  async apiEnqueueUploadRequest(body: UploadRequestBody): Promise<void> {
    return this.#agentRequest<void>('post', '/upload-requests', { body });
  }

  async apiListUploadRequests(): Promise<UploadRequestItems> {
    return this.#agentRequest<UploadRequestItems>('get', '/upload-requests');
  }

  async apiGetUploadRequest(id: string): Promise<UploadRequestItem> {
    return this.#agentRequest<UploadRequestItem>(
      'get',
      `/upload-requests/${id}`,
    );
  }

  async apiListUploadItemsForRequest(id: string): Promise<UploadAssetItems> {
    return this.#agentRequest<UploadAssetItems>(
      'get',
      `/upload-requests/${id}/uploads`,
    );
  }

  async apiListUploads(): Promise<UploadAssetItems> {
    return this.#agentRequest<UploadAssetItems>('get', '/uploads');
  }

  async apiGetUpload(id: string): Promise<UploadAssetItem> {
    return this.#agentRequest<UploadAssetItem>('get', `/uploads/${id}`);
  }

  async apiPatchUpload(
    id: string,
    state: { status: 'PAUSED' | 'UPLOADING' },
  ): Promise<UploadAssetItem> {
    return this.#agentRequest<UploadAssetItem>('patch', `/uploads/${id}`, {
      body: { state },
    });
  }

  async apiCancelUpload(id: string): Promise<void> {
    return this.#agentRequest<void>('delete', `/uploads/${id}`);
  }

  // ===== OAuth loopback endpoints =====

  /**
   * Poll for the result of a sign-in redirect. `pending` until the browser has landed
   * on the Agent's callback; a result is returned once and then forgotten by the Agent.
   */
  async pollOAuthSession(
    state: string,
    options?: GenericHttpRequestOptions,
  ): Promise<OAuthSessionPollResult> {
    let body: OAuthSessionResultBody;
    try {
      body = await this.#agentRequest<OAuthSessionResultBody>(
        'get',
        `/oauth/sessions/${encodeURIComponent(state)}`,
        {},
        options,
      );
    } catch (error) {
      if (error instanceof GenericHttpError) {
        if (error.response.status === 404) {
          return { type: 'pending' };
        }
        throw error;
      }
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      return { type: 'agent_not_running' };
    }

    if (body.status === 'complete' && body.code) {
      return { type: 'complete', code: body.code };
    }
    return {
      type: 'error',
      error: body.error ?? 'unknown_error',
      errorDescription: body.error_description,
    };
  }

  /** Abandon a sign-in. Best-effort: the Agent forgets it after its TTL anyway. */
  async cancelOAuthSession(state: string): Promise<void> {
    try {
      await this.#agentRequest<void>(
        'delete',
        `/oauth/sessions/${encodeURIComponent(state)}`,
      );
    } catch {
      // Nothing to recover; the session expires on its own.
    }
  }

  async #agentRequest<T>(
    method: HttpMethod,
    path: string,
    config: AgentRequestConfig = {},
    options?: GenericHttpRequestOptions,
  ): Promise<T> {
    if (!this.#metadata) {
      throw new Error(
        'Iconik Agent is not available. Call init() and check agent status before making requests.',
      );
    }
    const response = await this.transport(
      { method, url: `${this.baseUrl}${path}`, ...config },
      options,
    );
    // biome-ignore lint/plugin: Agent responses are trusted to match the endpoint's type.
    return response.body as T;
  }

  async tryDownload({
    body,
    params,
    autoLoginHeaders,
  }: TryDownloadOptions): Promise<TryDownloadRequestResponse> {
    const pingResult = await this.ping();
    if (!pingResult) {
      return { type: 'agent_not_running' };
    }

    if (autoLoginHeaders) {
      this.setAutoLoginHeaders(autoLoginHeaders);
    }

    const filteredBody: DownloadRequestBody = {};

    if (body.assets) {
      filteredBody.assets = prepareOutputPath(body.assets);
    }
    if (body.collections) {
      filteredBody.collections = body.collections;
    }
    if (body.playlists) {
      filteredBody.playlists = body.playlists;
    }
    if (this.hasFeature('DOWNLOAD_VERSION') && body.assetVersions) {
      filteredBody.assetVersions = body.assetVersions;
    }
    if (this.hasFeature('DOWNLOAD_BY_FILE_ID') && body.files) {
      filteredBody.files = prepareOutputPath(body.files);
    }
    if (this.hasFeature('DOWNLOAD_FILE_SET') && body.fileSets) {
      filteredBody.fileSets = body.fileSets;
    }

    if (Object.keys(filteredBody).length === 0) {
      throw new Error(
        'No valid download request body provided. At least one of assets, collections, assetVersions, playlists, files, or fileSets must be specified.',
      );
    }

    return this.apiCreateDownloadRequest(filteredBody, params)
      .then((response) => ({ type: 'success' as const, requests: response }))
      .catch((error) => ({ type: 'error' as const, error }));
  }
}
